import React, { useState } from 'react';
import { X, Send, Info } from 'lucide-react';
import { toast } from 'sonner';

const IvoryMailModal = ({ patient, onClose, onSend }) => {
  const [formData, setFormData] = useState({
    from: 'Current User',
    to: '',
    subject: '',
    attachment: patient ? `Admission Record for ${patient.lastName}, ${patient.firstName}` : '',
    date: new Date().toLocaleString(),
    message: '',
    sendTextNotification: false
  });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!formData.to) {
      toast.error('Please enter recipient');
      return;
    }
    
    setSending(true);
    try {
      // Simulate sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Message sent successfully');
      onSend && onSend(formData);
      onClose();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="bg-[#5bc0de] text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
          <h2 className="text-lg font-semibold">IvoryMail</h2>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* From */}
          <div className="flex items-center gap-4">
            <label className="text-gray-600 w-24 text-right">From:</label>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-[#5bc0de]">👤</span>
              <span className="text-gray-700">{formData.from}</span>
            </div>
          </div>

          {/* To */}
          <div className="flex items-center gap-4">
            <label className="text-gray-600 w-24 text-right">To:</label>
            <input
              type="text"
              value={formData.to}
              onChange={(e) => setFormData({...formData, to: e.target.value})}
              placeholder="Recipient(s)"
              className="flex-1 border border-gray-300 rounded px-3 py-2"
            />
          </div>

          {/* Subject */}
          <div className="flex items-center gap-4">
            <label className="text-gray-600 w-24 text-right">Subject:</label>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-gray-400">○</span>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="Subject"
                className="flex-1 border border-gray-300 rounded px-3 py-2"
              />
            </div>
          </div>

          {/* Attachment */}
          <div className="flex items-center gap-4">
            <label className="text-gray-600 w-24 text-right">Attachment:</label>
            <span className="text-[#5bc0de]">{formData.attachment}</span>
          </div>

          {/* Date */}
          <div className="flex items-center gap-4">
            <label className="text-gray-600 w-24 text-right">Date:</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">📅</span>
              <span className="text-gray-600">{formData.date}</span>
            </div>
          </div>

          {/* Message */}
          <div className="flex gap-4">
            <label className="text-gray-600 w-24 text-right pt-2">Message:</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={6}
              className="flex-1 border border-gray-300 rounded px-3 py-2 resize-none"
            />
          </div>

          {/* Send Text Notification */}
          <div className="flex items-center gap-4">
            <div className="w-24"></div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sendTextNotification}
                onChange={(e) => setFormData({...formData, sendTextNotification: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-gray-700">Send Text Notification</span>
              <Info className="w-4 h-4 text-[#5bc0de]" />
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-center gap-3">
          <button
            onClick={handleSend}
            disabled={sending}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <Send className="w-4 h-4 text-[#5bc0de]" />
            {sending ? 'Sending...' : 'Send'}
          </button>
          <button
            onClick={onClose}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <X className="w-4 h-4 text-[#d9534f]" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IvoryMailModal;
